import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

interface UseRevealOptions {
  /** How far into the viewport the element must travel, as a CSS margin. */
  rootMargin?: string
  /** Re-hide when it leaves again. Off by default: reveals happen once. */
  repeat?: boolean
}

interface RevealState<T extends HTMLElement> {
  ref: RefObject<T | null>
  isVisible: boolean
}

/**
 * Reveals an element the first time it scrolls into view.
 *
 * Uses IntersectionObserver rather than scroll handlers so there is no work
 * on the scroll thread, and disconnects once revealed. Where the observer is
 * unavailable the element starts visible — content must never be hidden by a
 * missing capability.
 *
 * Two safeguards stop content getting stranded invisible, both of which it
 * previously was:
 *
 *  - The negative bottom `rootMargin` makes the reveal wait until an element
 *    is properly in view. On a page too short to scroll, an element sitting
 *    inside that bottom inset could never satisfy it and stayed at opacity 0
 *    forever. So the callback also reveals anything already touching the real
 *    viewport, regardless of the margin.
 *  - If neither fires within a grace period — a detached tab, a zero-height
 *    parent, an observer that never reports — the element is shown anyway.
 *    A late reveal is a cosmetic loss; an invisible page is a broken one.
 */
export const useReveal = <T extends HTMLElement = HTMLDivElement>({
  rootMargin = '0px 0px -12% 0px',
  repeat = false,
}: UseRevealOptions = {}): RevealState<T> => {
  const ref = useRef<T | null>(null)
  const [isVisible, setIsVisible] = useState(
    () => typeof IntersectionObserver === 'undefined',
  )

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return

    let done = false
    const reveal = () => {
      if (done) return
      done = true
      setIsVisible(true)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return

        // Already touching the viewport: show it, whatever the margin says.
        const touchingViewport =
          entry.boundingClientRect.top < window.innerHeight &&
          entry.boundingClientRect.bottom > 0

        if (entry.isIntersecting || touchingViewport) {
          reveal()
          if (!repeat) observer.disconnect()
        } else if (repeat) {
          done = false
          setIsVisible(false)
        }
      },
      { rootMargin, threshold: 0 },
    )

    observer.observe(el)

    // Last resort, so nothing can stay hidden indefinitely.
    const failsafe = window.setTimeout(() => {
      reveal()
      if (!repeat) observer.disconnect()
    }, 1200)

    return () => {
      window.clearTimeout(failsafe)
      observer.disconnect()
    }
  }, [rootMargin, repeat])

  return { ref, isVisible }
}
